/**
 * Birth place input (input_subtype: birth_place) — Alpine component.
 * Moved from the inline script in components/birth-place-input.blade.php: inline
 * blade scripts die when the component first appears via a Livewire morph
 * (morphdom doesn't execute injected script tags), which left the whole field
 * dead — no autocomplete AND no way to save manual input.
 *
 * Manual entry needs no extra binding here: the visible input carries the
 * wire:model attribute itself, so Livewire persists every keystroke with or
 * without Google. Google Places is a progressive enhancement layered on top
 * when the central loader resolves.
 */
import loadGoogleMaps from '../lib/google-maps-loader';
import alvvaParsePlace from '../lib/google-place-parser';

export default function birthPlaceAutocomplete(modelValue) {
    return {
        autocomplete: null,
        pacContainers: [],
        modelValue: modelValue,

        init() {
            this.bindManualEntry();

            loadGoogleMaps()
                .then(() => {
                    this.initAutocomplete();
                    // Mirror the old window-load path: re-assert the saved
                    // value after the Autocomplete attach, which could leave
                    // the input blank on a return visit.
                    this.restoreSavedValue();
                })
                .catch(() => {}); // silent fallback — manual entry already works

            // Find and track Google's PAC containers
            this.trackPacContainers();

            // Put the saved value back if Livewire's deferred binding or the
            // Google Autocomplete attach left the input blank on a return
            // visit. Without this the field looked "reverted" on review, and
            // a blank commit could wipe the stored city. Runs only at load,
            // never while the client is editing.
            this.$nextTick(() => this.restoreSavedValue());
            setTimeout(() => this.restoreSavedValue(), 600);
        },

        /**
         * Always-on manual path: never depends on Google.
         *
         * Intentionally adds no listeners: unlike the address components, the
         * visible input here is wire:model-bound directly, so Livewire already
         * captures the raw typed text on every 'input' event — typed text
         * survives Continue without Enter/blur/selection. Adding a parallel
         * input/blur commit would double-dispatch into the same binding.
         */
        bindManualEntry() {
            // no-op — see docblock above
        },

        restoreSavedValue() {
            const input = this.$refs.birthPlaceInput;
            if (!input) return;
            if (input === document.activeElement) return; // don't fight the user
            if (!input.value && this.modelValue) {
                input.value = this.modelValue;
                input.dispatchEvent(new Event('input', { bubbles: true }));
            }
        },

        trackPacContainers() {
            // Check periodically for PAC containers and store references
            const checkForPacContainers = () => {
                const containers = document.querySelectorAll('.pac-container');
                if (containers.length > 0) {
                    this.pacContainers = Array.from(containers);
                }
            };

            // Check immediately and then periodically
            checkForPacContainers();
            setInterval(checkForPacContainers, 500);
        },

        clearFields() {
            const groupContainer = this.$refs.birthPlaceInput.closest('.space-y-6');
            if (groupContainer) {
                const inputs = groupContainer.querySelectorAll('input[wire\\:model\\.defer^="groupedResponses."]');
                inputs.forEach(input => {
                    input.value = '';
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                });
            }
            this.$refs.birthPlaceInput.focus();
        },

        closeDropdown() {
            const input = this.$refs.birthPlaceInput;
            if (!input) return;

            // Blur the input
            input.blur();

            // Force hide all PAC containers
            this.pacContainers.forEach(container => {
                container.style.display = 'none';
            });

            // Force hide any PAC containers by class
            const pacContainers = document.querySelectorAll('.pac-container');
            pacContainers.forEach(container => {
                container.style.display = 'none';
            });
        },

        initAutocomplete() {
            const input = this.$refs.birthPlaceInput;
            if (!input) return;

            this.autocomplete = new google.maps.places.Autocomplete(input, {
                fields: ["address_components", "formatted_address"],
                types: ["geocode"]
            });

            this.autocomplete.addListener("place_changed", () => {
                const place = this.autocomplete.getPlace();

                if (place.address_components) {
                    // Shared parser: country-aware city fallbacks (UK
                    // postal_town, sublocality wards, …). Birth place uses
                    // the state's long name in its "City, State, Country".
                    const p = alvvaParsePlace(place);
                    const city = p.city, state = p.state_long, country = p.country;

                    // Update the main input with formatted place
                    const formattedPlace = [city, state, country].filter(Boolean).join(', ');
                    if (formattedPlace) {
                        input.value = formattedPlace;
                        input.dispatchEvent(new Event('input', { bubbles: true }));
                    }

                    // Also update grouped responses if in a group
                    this.updateGroupedResponses(city, state, country);

                    // Then forcefully close the dropdown
                    setTimeout(() => {
                        this.closeDropdown();
                    }, 100);
                }
            });

            // Add click listener to document
            document.addEventListener('click', (e) => {
                // Check if click is outside both the input and any PAC container
                const isPacContainerClick = this.pacContainers.some(container =>
                    container.contains(e.target)
                );

                if (!input.contains(e.target) && !isPacContainerClick) {
                    this.closeDropdown();
                }
            });
        },

        updateGroupedResponses(city, state, country) {
            // Find all input fields in the same group and update them based on their labels
            const groupContainer = this.$refs.birthPlaceInput.closest('.space-y-6');
            if (groupContainer) {
                const inputs = groupContainer.querySelectorAll('input[wire\\:model\\.defer^="groupedResponses."]');

                inputs.forEach(input => {
                    const wireModel = input.getAttribute('wire:model.defer');
                    const uniqueId = wireModel.replace('groupedResponses.', '');

                    // Find the label for this input to determine what field it is
                    const label = this.findLabelForInput(input);

                    if (label) {
                        const labelLower = label.toLowerCase();

                        if (labelLower.includes('city') && city) {
                            input.value = city;
                            input.dispatchEvent(new Event('input', { bubbles: true }));
                        } else if ((labelLower.includes('state') || labelLower.includes('province')) && state) {
                            input.value = state;
                            input.dispatchEvent(new Event('input', { bubbles: true }));
                        } else if (labelLower.includes('country') && country) {
                            input.value = country;
                            input.dispatchEvent(new Event('input', { bubbles: true }));
                        }
                    }
                });

                // Fallback: try to update by position if label matching fails
                this.updateByPosition(inputs, city, state, country);
            } else {
                // No group container found
            }
        },

        updateByPosition(inputs, city, state, country) {
            // If we have exactly 3 inputs and the label matching didn't work,
            // try updating by position (assuming order: city, state, country)
            if (inputs.length === 3 && (city || state || country)) {
                const inputArray = Array.from(inputs);

                if (city && inputArray[0]) {
                    inputArray[0].value = city;
                    inputArray[0].dispatchEvent(new Event('input', { bubbles: true }));
                }

                if (state && inputArray[1]) {
                    inputArray[1].value = state;
                    inputArray[1].dispatchEvent(new Event('input', { bubbles: true }));
                }

                if (country && inputArray[2]) {
                    inputArray[2].value = country;
                    inputArray[2].dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
        },

        findLabelForInput(input) {
            // Find the label associated with this input
            // First try to find the label within the same space-y-2 container
            const container = input.closest('.space-y-2');
            if (container) {
                const label = container.querySelector('label');
                if (label) {
                    return label.textContent.trim();
                }
            }

            // If not found, try to find by for attribute
            const inputId = input.id;
            if (inputId) {
                const label = document.querySelector(`label[for="${inputId}"]`);
                if (label) {
                    return label.textContent.trim();
                }
            }

            // If still not found, try to find the previous sibling label
            let previousElement = input.previousElementSibling;
            while (previousElement) {
                if (previousElement.tagName === 'LABEL') {
                    return previousElement.textContent.trim();
                }
                previousElement = previousElement.previousElementSibling;
            }

            return '';
        }
    }
}
